import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { LoadingSection } from '../../layout/LoadingSection';
import type { MediaItem, MediaLoadingState } from '../../types';

interface HubspotTicketMediaSectionProps {
  banner?: MediaItem;
  featureGraphic?: MediaItem;
  screenshots: MediaItem[];
  videos: MediaItem[];
  mediaLoadingStates: MediaLoadingState;
  onBannerReady: () => void;
  onFeatureGraphicReady: () => void;
  onScreenshotReady: (index: number) => void;
  onVideosReady: () => void;
}

interface MediaImageCardProps {
  item: MediaItem;
  title: string;
  alt: string;
  aspectRatio: string;
  isLoading: boolean;
  onReady: () => void;
  onPreview: () => void;
  badge?: string;
}

function MediaImageCard({
  item,
  title,
  alt,
  aspectRatio,
  isLoading,
  onReady,
  onPreview,
  badge,
}: MediaImageCardProps) {
  return (
    <LoadingSection
      isLoading={isLoading}
      overlayStyle="blurred"
      spinnerPosition="center"
    >
      <div className="bg-neutral-800/50 rounded-lg overflow-hidden">
        <div
          className="relative cursor-pointer"
          onClick={onPreview}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onPreview();
          }}
        >
          <img
            src={item.uri}
            alt={alt}
            className="max-h-64 w-full object-cover"
            style={{ aspectRatio }}
            onLoad={onReady}
            onError={onReady}
          />
          {badge && (
            <div className="absolute top-1.5 right-1.5 bg-teal-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold">
              {badge}
            </div>
          )}
        </div>
        <div className="px-2 py-1 flex items-center justify-between">
          <span className="text-[11px] text-neutral-400">
            {title} ({item.width} x {item.height})
          </span>
          <a
            href={item.uri}
            target="_blank"
            rel="noopener noreferrer"
            className="p-0.5 text-neutral-400 hover:text-white transition-colors"
            title="Open in new tab"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon className="size-3.5 fill-current" />
          </a>
        </div>
      </div>
    </LoadingSection>
  );
}

interface ImagePreviewDialogProps {
  uri: string;
  alt: string;
  onClose: () => void;
}

function ImagePreviewDialog({ uri, alt, onClose }: ImagePreviewDialogProps) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 rounded-full bg-neutral-800/80 p-2 text-white hover:bg-neutral-700 transition-colors"
      >
        <CloseIcon className="size-6 fill-current" />
      </button>
      <button
        type="button"
        aria-label={alt}
        className="max-h-[90vh] max-w-[90vw] cursor-default border-0 bg-transparent p-0 leading-none"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={uri}
          alt={alt}
          className="block max-h-[90vh] max-w-[90vw] object-contain"
        />
      </button>
    </div>,
    document.body,
  );
}

export function HubspotTicketMediaSection({
  banner,
  featureGraphic,
  screenshots,
  videos,
  mediaLoadingStates,
  onBannerReady,
  onFeatureGraphicReady,
  onScreenshotReady,
  onVideosReady,
}: HubspotTicketMediaSectionProps) {
  const [previewImage, setPreviewImage] = useState<{
    uri: string;
    alt: string;
  } | null>(null);

  useEffect(() => {
    if (!previewImage) return;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewImage(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewImage]);

  if (
    !banner &&
    !featureGraphic &&
    screenshots.length === 0 &&
    videos.length === 0
  ) {
    return null;
  }

  return (
    <>
      {banner && (
        <MediaImageCard
          item={banner}
          title="Banner"
          alt="App banner"
          aspectRatio="2/1"
          isLoading={mediaLoadingStates.banner}
          onReady={onBannerReady}
          onPreview={() =>
            setPreviewImage({ uri: banner.uri, alt: 'App banner' })
          }
        />
      )}

      {featureGraphic && (
        <MediaImageCard
          item={featureGraphic}
          title="Feature Graphic"
          alt="Feature graphic"
          aspectRatio="1/1"
          isLoading={mediaLoadingStates.featureGraphic}
          onReady={onFeatureGraphicReady}
          onPreview={() =>
            setPreviewImage({
              uri: featureGraphic.uri,
              alt: 'Feature graphic',
            })
          }
          badge="Featured"
        />
      )}

      {screenshots.length > 0 && (
        <LoadingSection
          isLoading={mediaLoadingStates.screenshots.size > 0}
          overlayStyle="blurred"
          spinnerPosition="center"
        >
          <div className="bg-neutral-800/50 rounded-lg p-2 space-y-1.5">
            <h3 className="text-xs font-semibold text-white">
              Screenshots ({screenshots.length})
            </h3>
            <div className="flex snap-x gap-2 overflow-x-auto pb-1">
              {screenshots.map((screenshot, index) => (
                <button
                  key={`${screenshot.uri}-${index}`}
                  type="button"
                  onClick={() =>
                    setPreviewImage({
                      uri: screenshot.uri,
                      alt: `Screenshot ${index + 1}`,
                    })
                  }
                  className="group relative block shrink-0 snap-start overflow-hidden rounded border border-neutral-700 bg-neutral-900 transition-colors hover:border-teal-400"
                >
                  <img
                    src={screenshot.uri}
                    alt={`Screenshot ${index + 1}`}
                    className="h-56 w-auto max-w-[70vw] object-contain sm:h-64"
                    style={{
                      aspectRatio:
                        screenshot.width && screenshot.height
                          ? `${screenshot.width} / ${screenshot.height}`
                          : undefined,
                    }}
                    loading="lazy"
                    onLoad={() => onScreenshotReady(index)}
                    onError={() => onScreenshotReady(index)}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-[10px] font-medium">
                      Preview
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </LoadingSection>
      )}

      {videos.length > 0 && (
        <LoadingSection
          isLoading={mediaLoadingStates.videos}
          overlayStyle="blurred"
          spinnerPosition="center"
          allowInteractionWhileLoading
        >
          <div className="bg-neutral-800/50 rounded-lg p-2 space-y-1.5">
            <h3 className="text-xs font-semibold text-white">
              Videos ({videos.length})
            </h3>
            <div className="space-y-1.5">
              {videos.map((video, index) => (
                <div
                  key={`${video.uri}-${index}`}
                  className="rounded overflow-hidden bg-neutral-900 border border-neutral-700"
                >
                  <video
                    src={video.uri}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full"
                    style={{ maxHeight: '260px' }}
                    onLoadedMetadata={onVideosReady}
                    onError={onVideosReady}
                  >
                    <track kind="captions" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="px-2 py-1 flex items-center justify-between text-[11px]">
                    <span className="text-neutral-400">
                      {video.width} x {video.height}
                    </span>
                    <a
                      href={video.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:text-teal-300 transition-colors"
                    >
                      Open
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </LoadingSection>
      )}

      {previewImage && (
        <ImagePreviewDialog
          uri={previewImage.uri}
          alt={previewImage.alt}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </>
  );
}
